// CARE PROGRAMMES — shared rules for the programme builder, the patient's
// Programme card, the desk strip and the health record's Programmes section
// (doc/care-programmes-plan.md, Round B).
//
// NOTHING HERE IS SPECIALITY-SPECIFIC (owner, 26 Sep 2026). There is no
// milestone list, no starter programme and no named programme: a doctor or
// the hospital admin defines every programme — its name, what the start date
// is called, how long it runs and every STOP (name, day, ± window).
//
// A PROGRAMME IS A LINE OF VISITS (owner, 21 Oct 2026; doc/programme-visits.md).
// Each stop is one visit; a stop is done when its LINKED appointment is
// completed, or by the doctor's hand tick. Check sets are NOT part of a
// programme any more — a stop carries no set, and readings never count. This module only knows the server's LIMITS, so a form
// can refuse what the server would refuse before a request is made, and how
// to print the TIMELINE the server computes. The server stays the authority:
// every rule is re-checked there and its answer (with `index` / `field`) is
// shown where it points. The timeline is never recomputed here.
import { formatDay } from '../components/health/healthFormat';
import { tidyName } from './tidyName';

// Mirrors the contract's "Limits" table (validated server-side).
export const LIMITS = {
    name: 120,
    startLabel: 60,
    meantFor: 80,
    durationMax: 3660,
    milestonesMin: 1,
    milestonesMax: 100,
    milestoneName: 120,
    offsetMax: 3660,
    windowMax: 365,
    windowDefault: 7,
    note: 500,
    outcome: 500,
    lifeEventTitle: 255,
    startPastDays: 3660,
    startFutureDays: 366,
};

/** The five stop states (doc/programme-visits.md), in the order a legend reads them. */
export const STATES = ['done', 'booked', 'open', 'upcoming', 'late'];

/** Trim and collapse inner whitespace, as the server does before comparing. */
export const collapse = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/** Only digits survive a number box (no sign, no decimal: days are whole). */
export const digitsOnly = (value) => String(value ?? '').replace(/\D+/g, '').slice(0, 5);

let uidCounter = 0;
const nextUid = () => {
    uidCounter += 1;
    return `new-${uidCounter}`;
};

/* ---------------------------------------------------------------------------
 * Days and weeks. The server takes DAYS only; a client offering weeks sends
 * weeks × 7 + days (contract §0). A span in the editor is { weeks, days } as
 * typed: in 'days' mode only `days` is used (the whole number of days), in
 * 'weeks' mode `weeks` + `days` (0–6).
 * ------------------------------------------------------------------------- */

const toInt = (text) => {
    const s = String(text ?? '').trim();
    if (!/^\d+$/.test(s)) return NaN;
    return Number(s);
};

/** A span as typed → whole days, or NaN when it is not a valid span. */
export const spanToDays = (span, unit) => {
    const weeksText = String(span?.weeks ?? '').trim();
    const daysText = String(span?.days ?? '').trim();
    if (unit === 'weeks') {
        if (weeksText === '' && daysText === '') return NaN;
        const w = weeksText === '' ? 0 : toInt(weeksText);
        const d = daysText === '' ? 0 : toInt(daysText);
        if (!Number.isFinite(w) || !Number.isFinite(d) || d > 6) return NaN;
        return w * 7 + d;
    }
    return daysText === '' ? NaN : toInt(daysText);
};

/** Whole days → a span to show in `unit`. */
export const daysToSpan = (n, unit) => {
    if (!Number.isInteger(n) || n < 0) return { weeks: '', days: '' };
    if (unit === 'weeks') return { weeks: String(Math.floor(n / 7)), days: n % 7 ? String(n % 7) : '' };
    return { weeks: '', days: String(n) };
};

/* ---------------------------------------------------------------------------
 * The builder's working copy of a programme.
 * ------------------------------------------------------------------------- */

export const emptyMilestone = (unit = 'days') => ({
    uid: nextUid(),
    key: null,
    name: '',
    offset: { weeks: '', days: '' },
    windowDays: String(LIMITS.windowDefault),
    unit,
});

export const emptyProgrammeDraft = () => ({
    name: '',
    startLabel: '',
    meantFor: '',
    unit: 'days',
    openEnded: false,
    duration: { weeks: '', days: '' },
    milestones: [emptyMilestone('days')],
});

/**
 * Which unit a saved programme reads best in: weeks when every span in it is
 * a whole number of weeks (and there is at least one), days otherwise. The
 * doctor can switch either way; nothing is lost.
 */
export const preferredUnit = (programme) => {
    const spans = [
        ...(Number.isInteger(programme?.durationDays) ? [programme.durationDays] : []),
        ...(programme?.milestones || []).map((m) => m.offsetDays).filter(Number.isInteger),
    ];
    if (!spans.length || spans.every((n) => n === 0)) return 'days';
    return spans.every((n) => n % 7 === 0) ? 'weeks' : 'days';
};

/** A programme as the server sent it → the editor's working copy. */
export const programmeToDraft = (programme) => {
    const unit = preferredUnit(programme);
    const duration = Number.isInteger(programme?.durationDays) ? programme.durationDays : null;
    return {
        name: programme?.name || '',
        startLabel: programme?.startLabel || '',
        meantFor: programme?.meantFor || '',
        unit,
        openEnded: duration === null,
        duration: duration === null ? { weeks: '', days: '' } : daysToSpan(duration, unit),
        milestones: [...(programme?.milestones || [])]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((m) => ({
                uid: m.key || nextUid(),
                key: m.key || null,
                name: m.name || '',
                offset: daysToSpan(Number.isInteger(m.offsetDays) ? m.offsetDays : 0, unit),
                windowDays: String(Number.isInteger(m.windowDays) ? m.windowDays : LIMITS.windowDefault),
                unit,
            })),
    };
};

/**
 * Switch the whole programme between days and weeks. Every span that is valid
 * is re-split in the new unit (day 171 ↔ week 24 + 3 days); one that is not
 * valid yet is cleared, since it has no number to carry over.
 */
export const switchDraftUnit = (draft, unit) => {
    if (!draft || draft.unit === unit) return draft;
    const convert = (span) => daysToSpan(spanToDays(span, draft.unit), unit);
    return {
        ...draft,
        unit,
        duration: draft.openEnded ? draft.duration : convert(draft.duration),
        milestones: draft.milestones.map((m) => ({ ...m, offset: convert(m.offset), unit })),
    };
};

/** A window as typed; blank means the server's default (7). */
export const windowOf = (m) => {
    const text = String(m?.windowDays ?? '').trim();
    return text === '' ? LIMITS.windowDefault : toInt(text);
};

/**
 * The working copy → the POST / PUT body. Existing stops send their `key`
 * back (hand ticks and appointment links of started programmes are stored by
 * key); new ones send none — the server generates it. `position` is the array
 * order, so it is not sent. A stop is name + day + ± window: no `setId` is
 * ever sent (owner, 21 Oct 2026 — check sets are not part of a programme).
 */
export const draftToBody = (draft, ownerType) => {
    const body = {
        name: collapse(draft.name),
        startLabel: collapse(draft.startLabel),
        durationDays: draft.openEnded ? null : spanToDays(draft.duration, draft.unit),
        milestones: draft.milestones.map((m) => {
            const out = {};
            if (m.key) out.key = m.key;
            out.name = collapse(m.name);
            out.offsetDays = spanToDays(m.offset, draft.unit);
            out.windowDays = windowOf(m);
            return out;
        }),
    };
    if (ownerType === 'tenant') body.meantFor = collapse(draft.meantFor) || null;
    return body;
};

/**
 * What the server would refuse in a programme definition, as i18n keys under
 * `programmes.` (the component translates them):
 *   { name, startLabel, meantFor, duration, milestones,
 *     byIndex: { [i]: { name, offset, window } } }
 * `offset` may be { key, params } when the sentence names numbers.
 * An empty object means "nothing to refuse".
 */
export const validateProgrammeDraft = (draft, { ownerType = 'doctor' } = {}) => {
    const errors = {};
    const name = collapse(draft?.name);
    if (!name) errors.name = 'err_name_required';
    else if (name.length > LIMITS.name) errors.name = 'err_name_long';

    const startLabel = collapse(draft?.startLabel);
    if (!startLabel) errors.startLabel = 'err_start_label_required';
    else if (startLabel.length > LIMITS.startLabel) errors.startLabel = 'err_start_label_long';

    if (ownerType === 'tenant' && collapse(draft?.meantFor).length > LIMITS.meantFor) errors.meantFor = 'err_meant_for_long';

    let duration = null;
    if (!draft?.openEnded) {
        duration = spanToDays(draft?.duration, draft?.unit);
        if (!Number.isInteger(duration) || duration < 1 || duration > LIMITS.durationMax) {
            errors.duration = 'err_duration';
            duration = null;
        }
    }

    const milestones = draft?.milestones || [];
    if (milestones.length < LIMITS.milestonesMin) errors.milestones = 'err_milestones_required';
    else if (milestones.length > LIMITS.milestonesMax) errors.milestones = 'err_too_many_milestones';

    const seen = new Set();
    const byIndex = {};
    milestones.forEach((m, index) => {
        const e = {};
        const mName = collapse(m.name);
        if (!mName) e.name = 'err_milestone_name_required';
        else if (mName.length > LIMITS.milestoneName) e.name = 'err_milestone_name_long';
        else if (seen.has(mName.toLowerCase())) e.name = 'err_milestone_name_duplicate';
        else seen.add(mName.toLowerCase());

        const offset = spanToDays(m.offset, draft?.unit);
        if (!Number.isInteger(offset) || offset < 0 || offset > LIMITS.offsetMax) e.offset = 'err_offset';
        else if (duration !== null && offset > duration) e.offset = { key: 'err_offset_beyond', params: { day: offset, duration } };

        const win = windowOf(m);
        if (!Number.isInteger(win) || win < 0 || win > LIMITS.windowMax) e.window = 'err_window';

        if (Object.keys(e).length) byIndex[index] = e;
    });
    if (Object.keys(byIndex).length) errors.byIndex = byIndex;
    return errors;
};

export const hasProgrammeErrors = (errors) => Boolean(errors && (errors.name || errors.startLabel || errors.meantFor
    || errors.duration || errors.milestones || (errors.byIndex && Object.keys(errors.byIndex).length)));

/** Hospital programmes grouped by their free "meant for" label (null = general). */
export const groupByMeantFor = (programmes) => {
    const groups = new Map();
    (programmes || []).forEach((p) => {
        const label = collapse(p.meantFor) || '';
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(p);
    });
    return [...groups.entries()]
        .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
        .map(([label, list]) => ({ label, programmes: list }));
};

/** Milestones in TIMELINE order: by offset, then builder position. */
export const byOffset = (milestones) => [...(milestones || [])]
    .sort((a, b) => ((a.offsetDays ?? 0) - (b.offsetDays ?? 0)) || ((a.position ?? 0) - (b.position ?? 0)));

/* ---------------------------------------------------------------------------
 * Start dates.
 * ------------------------------------------------------------------------- */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'YYYY-MM-DD' → epoch day number, or NaN for anything that is not a real date. */
export const dayNumber = (value) => {
    const m = DATE_RE.exec(String(value || ''));
    if (!m) return NaN;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const t = Date.UTC(y, mo - 1, d);
    const dt = new Date(t);
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return NaN;
    return Math.floor(t / 86400000);
};

/** Today as 'YYYY-MM-DD' in the viewer's own calendar. */
export const todayIso = (now = new Date()) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * A start date the server would take: a real date from ten years back to one
 * year ahead (contract "startDate"). i18n key under `programmes.` or ''.
 * Judged against the viewer's today; the hospital's own day decides for real.
 */
export const startDateError = (value, today = todayIso()) => {
    const n = dayNumber(value);
    if (!Number.isFinite(n)) return 'err_start_date';
    const t = dayNumber(today);
    if (n < t - LIMITS.startPastDays || n > t + LIMITS.startFutureDays) return 'err_start_date_range';
    return '';
};

/** "Programme — Outcome", cut to the life-event title limit (the server's default). */
export const defaultLifeEventTitle = (name, outcome) => {
    const text = `${collapse(name)} — ${collapse(outcome)}`;
    return text.slice(0, LIMITS.lifeEventTitle);
};

/* ---------------------------------------------------------------------------
 * Printing a timeline (contract §1 "Timeline object").
 * ------------------------------------------------------------------------- */

/** "Week 24 + 3 days" / "Week 8" / "Week 3 + 1 day". */
export const weekDayLabel = (weeks, days, t) => {
    const w = Number.isInteger(weeks) ? weeks : 0;
    const d = Number.isInteger(days) ? days : 0;
    if (d === 0) return t('programmes.week_only', { weeks: w });
    if (d === 1) return t('programmes.week_one_day', { weeks: w });
    return t('programmes.week_days', { weeks: w, days: d });
};

/** "Duration: 280 days (40 weeks)" / "Duration: 90 days" / "Open-ended". */
export const durationLine = (days, t) => {
    if (!Number.isInteger(days)) return t('programmes.duration_open');
    if (days === 1) return t('programmes.duration_one');
    if (days % 7 === 0 && days >= 14) return t('programmes.duration_weeks', { n: days, weeks: days / 7 });
    return t('programmes.duration_days', { n: days });
};

/**
 * When a template's milestone falls and its window: "Week 8 · ±7 d" for a
 * whole number of weeks, "Day 30 · ±3 d" otherwise.
 */
export const offsetLine = (m, t) => {
    const n = Number.isInteger(m?.offsetDays) ? m.offsetDays : 0;
    const when = n > 0 && n % 7 === 0 ? t('programmes.week_only', { weeks: n / 7 }) : t('programmes.offset_day', { n });
    const w = Number.isInteger(m?.windowDays) ? m.windowDays : LIMITS.windowDefault;
    return `${when} · ${t('programmes.window_short', { n: w })}`;
};

/** Where the programme is today: "Week 24 + 3 days", or "Starts in 5 days". */
export const progressLabel = (timeline, t) => {
    if (!timeline) return '';
    if (timeline.started === false) {
        const n = Math.abs(Number(timeline.dayIndex) || 0);
        if (n === 1) return t('programmes.starts_tomorrow');
        return t('programmes.starts_in', { n });
    }
    return weekDayLabel(timeline.weeks, timeline.days, t);
};

/** When a milestone falls: "Week 8 + 3 days" (from its offset). */
export const dueWeekLabel = (m, t) => weekDayLabel(m?.dueWeek, m?.dueDayOfWeek, t);

/** "Due today", "Due in 4 days", "3 days late" — from `daysUntilDue`. */
export const dueInLabel = (m, t) => {
    const n = Number(m?.daysUntilDue);
    if (!Number.isFinite(n)) return '';
    if (n === 0) return t('programmes.due_today');
    if (n === 1) return t('programmes.due_tomorrow');
    if (n > 1) return t('programmes.due_in', { n });
    if (n === -1) return t('programmes.due_yesterday');
    return t('programmes.due_ago', { n: -n });
};

/** "27 May – 10 Jun 2026" style window, both edges inclusive. */
export const windowLabel = (m) => {
    const a = formatDay(m?.windowStart);
    const b = formatDay(m?.windowEnd);
    if (!a || !b) return '';
    return a === b ? a : `${a} – ${b}`;
};

/** "Dr Lakshmi Menon · Chitra Clinic" — who runs it and where it started. */
export const provenanceLine = (p, t) => {
    const parts = [];
    if (p?.doctorName) parts.push(t('programmes.by_doctor', { name: tidyName(p.doctorName) }));
    if (p?.hospitalName) parts.push(p.hospitalName);
    return parts.join(' · ');
};

/** 0–100, for a progress bar; null when the programme is open-ended. */
export const progressPercent = (timeline) => {
    const p = Number(timeline?.progress);
    if (timeline?.progress === null || timeline?.progress === undefined || !Number.isFinite(p)) return null;
    return Math.max(0, Math.min(100, Math.round(p * 100)));
};

/* ---------------------------------------------------------------------------
 * THE STOP LINE (doc/programme-visits.md, "Server contract"). Every client
 * draws a programme as a line of stops — the Start, then Stop 1, Stop 2… —
 * each `done` / `booked` / `open` / `upcoming` / `late`, as the SERVER
 * computed it. Two shapes arrive: the booking line (`stops` on the Line) and
 * the hospital-side timeline (`timeline.milestones`, or `timeline.stops`).
 * `programmeStops` turns either into the one list the line component draws;
 * nothing is recomputed — an older answer that still says `due` reads `open`.
 * ------------------------------------------------------------------------- */

const STOP_STATE_SET = new Set(STATES);
const stopState = (state) => (state === 'due' ? 'open' : STOP_STATE_SET.has(state) ? state : 'upcoming');

const doneByOf = (s) => {
    if (s.doneBy === 'visit' || s.doneBy === 'tick') return s.doneBy;
    if (s.evidence?.type === 'manual') return 'tick';
    if (s.evidence?.type === 'visit') return 'visit';
    return null;
};

/** One stop, in the contract's words, whatever shape it came in. */
export const normaliseStop = (s, fallbackNumber = null) => {
    const state = stopState(s?.state);
    const kind = s?.kind === 'start' ? 'start' : 'stop';
    return {
        key: s?.key || null,
        kind,
        number: Number.isInteger(s?.number) ? s.number : fallbackNumber,
        name: s?.name || '',
        dueDate: s?.dueDate || null,
        windowFrom: s?.windowFrom || s?.windowStart || null,
        windowTo: s?.windowTo || s?.windowEnd || null,
        state,
        appointment: s?.appointment || null,
        doneAt: s?.doneAt || (state === 'done' ? (s?.evidence?.date || s?.evidence?.at || null) : null),
        doneBy: doneByOf(s || {}),
        // A hand tick's note — hospital-internal and record views only (the
        // desk projection carries none).
        note: s?.note || (s?.evidence?.type === 'manual' ? s.evidence.note || null : null),
    };
};

/**
 * A booking Line or a patient programme → its stops, Start first. A timeline
 * that does not list its Start gets one drawn from the start date (done once
 * the programme has started), so every line begins where the doctor began it.
 */
export const programmeStops = (programme) => {
    const p = programme || {};
    const given = Array.isArray(p.stops) ? p.stops : Array.isArray(p.timeline?.stops) ? p.timeline.stops : null;
    if (given) {
        let n = 0;
        return given.map((s) => {
            if (s?.kind !== 'start') n += 1;
            return normaliseStop(s, s?.kind === 'start' ? 0 : n);
        });
    }
    const milestones = byOffset(p.timeline?.milestones);
    const out = [];
    const startDate = p.startDate || p.timeline?.startDate || null;
    if (!milestones.some((m) => m?.kind === 'start') && startDate) {
        const started = p.timeline?.started !== false;
        out.push({
            key: 'start', kind: 'start', number: 0, name: p.startLabel || '', dueDate: startDate,
            windowFrom: null, windowTo: null, state: started ? 'done' : 'upcoming', appointment: null,
            doneAt: started ? startDate : null, doneBy: null, note: null,
        });
    }
    let n = 0;
    milestones.forEach((m) => {
        if (m?.kind !== 'start') n += 1;
        out.push(normaliseStop(m, m?.kind === 'start' ? 0 : n));
    });
    return out;
};

/** "Start" / "Stop 2". */
export const stopLabel = (stop, t) => (stop?.kind === 'start'
    ? t('programmes.stop_start')
    : t('programmes.stop_n', { n: stop?.number ?? '' }));

/** "28 Nov – 8 Dec 2026" — a stop's (or a nextWindow's) window, both edges inclusive. */
export const rangeLabel = (from, to) => {
    const a = formatDay(from);
    const b = formatDay(to);
    if (!a || !b) return a || b || '';
    return a === b ? a : `${a} – ${b}`;
};

/**
 * May an appointment on `date` ('YYYY-MM-DD') be a programme visit, given the
 * line's `nextWindow`? 'ok' inside the window (both edges count), 'before' /
 * 'after' outside it, 'none' when there is no stop to book.
 */
export const windowGate = (date, nextWindow) => {
    if (!nextWindow?.from || !nextWindow?.to) return 'none';
    const d = dayNumber(date);
    const a = dayNumber(nextWindow.from);
    const b = dayNumber(nextWindow.to);
    if (!Number.isFinite(d) || !Number.isFinite(a) || !Number.isFinite(b)) return 'none';
    if (d < a) return 'before';
    if (d > b) return 'after';
    return 'ok';
};

/** The stop the next programme visit would take (the server's `nextStopKey`). */
export const nextStopOf = (line) => {
    if (!line?.nextStopKey) return null;
    return programmeStops(line).find((s) => s.key === line.nextStopKey) || null;
};

/**
 * A linked appointment's programme as one line: "Diabetes care · Stop 2"
 * (from the appointment's `programme` object). '' when it is not linked.
 */
export const appointmentProgrammeLine = (programme, t) => {
    if (!programme || !programme.patientProgrammeId) return '';
    const stop = Number.isInteger(programme.stopNumber)
        ? t('programmes.stop_n', { n: programme.stopNumber })
        : (programme.stopName || '');
    return [programme.name, stop].filter(Boolean).join(' · ');
};

/** The booking body's `programme` (contract "Booking body"), or null for a different problem. */
export const bookingProgrammeBody = (choice) => {
    if (!choice?.patientProgrammeId) return null;
    if (choice.purpose === 'emergency') {
        return { patientProgrammeId: choice.patientProgrammeId, purpose: 'emergency', emergencyReason: collapse(choice.emergencyReason) };
    }
    if (choice.purpose === 'programme') return { patientProgrammeId: choice.patientProgrammeId, purpose: 'programme' };
    return null;
};

/** Emergency reasons are free text; the server takes up to this many characters. */
export const EMERGENCY_REASON_MAX = 500;
